import {
  type TableBlockModel,
  TableModelFlavour,
} from '@blocksuite/affine-model';
import type { DeltaInsert, Store } from '@blocksuite/store';
import { Text } from '@blocksuite/store';

import { DEFAULT_COLUMN_WIDTH, MIN_COLUMN_WIDTH } from '../styles.js';

/** Fractional-index order, the same comparison the table block uses. */
function byOrder(a: { order: string }, b: { order: string }) {
  return a.order > b.order ? 1 : a.order < b.order ? -1 : 0;
}

/**
 * Rebuilds an `affine:table` as an `affine:rich-table`, one paragraph per cell
 * carrying the original cell's delta so inline formatting and `@`-references
 * survive. One-way: the old table is deleted once the new one is in place.
 *
 * @returns the new table's id, or null when nothing was converted.
 */
export function convertTableToRichTable(
  store: Store,
  table: TableBlockModel
): string | null {
  if (store.readonly) return null;
  if (table.flavour !== TableModelFlavour) return null;

  const parent = table.parent;
  if (!parent) return null;

  const columns = Object.values(table.props.columns).sort(byOrder);
  const rows = Object.values(table.props.rows).sort(byOrder);
  if (columns.length === 0 || rows.length === 0) return null;

  // Both blocks measure columns in pixels, so widths carry straight over.
  const columnWidths = columns.map(column =>
    Math.max(column.width ?? DEFAULT_COLUMN_WIDTH, MIN_COLUMN_WIDTH)
  );

  const tableId = store.addBlock(
    'affine:rich-table',
    { columnWidths, headerRow: true },
    parent.id,
    parent.children.indexOf(table)
  );

  for (const row of rows) {
    const rowId = store.addBlock('affine:rich-table-row', {}, tableId);
    for (const column of columns) {
      const cellId = store.addBlock('affine:rich-table-cell', {}, rowId);
      const cell = table.props.cells[`${row.rowId}:${column.columnId}`];
      // `toDelta()` types `insert` as optional; a cell's deltas are always
      // text inserts, so the empty ones are simply dropped.
      const delta = (cell?.text?.toDelta() ?? []).filter(
        (op): op is DeltaInsert => typeof op.insert === 'string'
      );
      store.addBlock('affine:paragraph', { text: new Text(delta) }, cellId);
    }
  }

  store.deleteBlock(table);
  return tableId;
}
